<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import BinarySourceTree from './BinarySourceTree.vue'
import FieldDropdown from './FieldDropdown.vue'
import { emptyReference, referenceCrumbs, referenceFromScopePath, scopePathOf } from './binary-reference'
import type { BinaryReference } from '@/types'
import type { ScopeNode } from '@/types/bbel-types'
import { BBEL_SCOPE_KEY, EMPTY_BBEL_SCOPE, type BbelScopeContext } from '@/views/builder/composables/bbel-context'

/**
 * ADR-034 binary source picker. It authors a {@link BinaryReference} by browsing the upstream-service scope
 * tree the BBEL editor already uses. The field shows the chosen reference as the service's name followed by
 * its path, and opens the tree in a menu under it. The tree offers only the content-bearing projections
 * (`output` and `raw`) and leaves out anything a bare structural path cannot reach: array-element steps
 * (`[0]`) and bracket keys (`["x-id"]`). Clicking a projection binds the whole output; clicking a member
 * binds that node, and an array member binds the whole array.
 *
 * Two slots let a caller whose value is more than a reference reuse the menu: `field` replaces the button,
 * and `footer` replaces the Clear link.
 */
const props = defineProps<{
  modelValue: BinaryReference
  /** Accessible name of the field, such as "Source". */
  label?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: BinaryReference]
}>()

const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const scope = computed(() => scopeCtx?.value ?? EMPTY_BBEL_SCOPE)

/** Drop nodes a bare path can't address (a `[` in the path = an array-element step or a bracket key)
 *  and their subtrees, so every remaining node is a clean dot-member chain. */
function pruneToBarePaths(nodes: ScopeNode[]): ScopeNode[] {
  return nodes
    .filter((n) => !n.path.includes('['))
    .map((n) => {
      const children = n.children ? pruneToBarePaths(n.children) : undefined
      return { ...n, children: children?.length ? children : undefined }
    })
}

// Each upstream service, keeping only its output and raw projections, pruned to nodes a reference can name.
const sourceNodes = computed<ScopeNode[]>(() => {
  const services = scope.value.roots.find((r) => r.segment === 'services')
  return (services?.children ?? [])
    .map((svc) => ({
      ...svc,
      children: pruneToBarePaths((svc.children ?? []).filter((p) => referenceFromScopePath(p.path) !== undefined)),
    }))
    .filter((svc) => (svc.children?.length ?? 0) > 0)
})

const expanded = ref<Set<string>>(new Set())
function toggleExpanded(path: string) {
  const next = new Set(expanded.value)
  next.has(path) ? next.delete(path) : next.add(path)
  expanded.value = next
}

function pick(scopePath: string, close: () => void) {
  const reference = referenceFromScopePath(scopePath)
  if (!reference) return
  emit('update:modelValue', reference)
  close()
}

// Clear writes the empty reference. Writing nothing would not clear: the save skips an absent value and the
// stored source would stay.
function clear(close: () => void) {
  emit('update:modelValue', emptyReference())
  close()
}

const hasSelection = computed(() => props.modelValue.serviceId !== '')

// The chosen service's browse label, or its id when the service has since been removed.
const crumbs = computed(() => {
  const service = sourceNodes.value.find((s) => s.segment === props.modelValue.serviceId)
  return referenceCrumbs(props.modelValue, service?.label ?? props.modelValue.serviceId)
})

// The button's accessible name: the field's label and the chosen value the button shows.
const accessibleName = computed(() => {
  const chosen = hasSelection.value ? crumbs.value.join(', ') : 'Choose a source'
  return props.label ? `${props.label}: ${chosen}` : chosen
})

// The chosen node's scope path, so the tree highlights it.
const selectedPath = computed(() => scopePathOf(props.modelValue))
</script>

<template>
  <FieldDropdown>
    <template #default="{ open, toggle }">
      <slot name="field" :open="open" :toggle="toggle">
        <button
          type="button"
          class="form-control d-flex align-items-center gap-2 text-start"
          :aria-expanded="open"
          :aria-label="accessibleName"
          @click="toggle"
        >
          <span class="flex-grow-1 text-truncate">
            <span v-if="!hasSelection" class="text-body-secondary">Choose a source</span>
            <template v-else>
              <template v-for="(crumb, index) in crumbs" :key="index">
                <i v-if="index > 0" class="bi bi-chevron-right small text-body-secondary mx-1" aria-hidden="true"></i>
                <span :class="{ 'font-monospace': index > 0 }">{{ crumb }}</span>
              </template>
            </template>
          </span>
          <i class="bi bi-chevron-down small text-body-secondary" aria-hidden="true"></i>
        </button>
      </slot>
    </template>

    <template #menu="{ close }">
      <div v-if="sourceNodes.length" class="px-1">
        <BinarySourceTree
          :nodes="sourceNodes"
          :expanded="expanded"
          :selected-path="selectedPath"
          @select="pick($event, close)"
          @toggle="toggleExpanded"
        />
      </div>
      <p v-else class="text-muted small mb-0 px-1">
        No upstream service to bind. Add a service before this one, run it once to sample its output,
        then pick the data to stream.
      </p>
      <div v-if="$slots.footer || hasSelection" class="border-top mt-1 pt-1 px-1">
        <slot name="footer" :close="close">
          <button type="button" class="btn btn-link btn-sm p-0" @click="clear(close)">Clear</button>
        </slot>
      </div>
    </template>
  </FieldDropdown>
</template>
